
const SQL = [
  {
    "id": 1,
    "html_title": "intro",
    "bootstrap_head_desc": [
      {
        "bootstrap_title_heading": "What is SQL?",
        "bootstrap_title_desc": "SQL stands for Structured Query Language. It is used to store, retrieve, update, and manage data in a relational database. SQL helps users interact with databases using readable commands like SELECT, INSERT, UPDATE, DELETE."
      },
      {
        "bootstrap_title_heading": "What is a Relational Database?",
        "bootstrap_title_desc": "A relational database stores data in tables, where each table has rows (records) and columns (fields). Microsoft SQL Server is one of the most popular relational database management systems.\n\nExample:\n| id | name   | grade |\n|----|--------|-------|\n| 1  | Alice  | A     |\n| 2  | Bob    | B     |"
      },
      {
        "bootstrap_title_heading": "Installing SQL Server",
        "bootstrap_title_desc": "To get started with MS SQL:\n\n1. **Download SQL Server**: https://www.microsoft.com/en-us/sql-server/sql-server-downloads\n2. **Install SQL Server Express** (Free version)\n3. **Install SQL Server Management Studio (SSMS)**: https://aka.ms/ssmsfullsetup\n\nSSMS is a powerful graphical interface to interact with your SQL Server databases."
      },
      {
        "bootstrap_title_heading": "Basic SQL Syntax (MS SQL)",
        "bootstrap_title_desc": "SQL keywords are not case-sensitive, but it's standard to write them in UPPERCASE for readability.\n\nExample:\n```sql\nSELECT name FROM Students;\n```\nThis retrieves the 'name' column from the 'Students' table in MS SQL Server."
      },
      {
        "bootstrap_title_heading": "SELECT Statement Example (MS SQL)",
        "bootstrap_title_desc": "Imagine you have a table called Products:\n\n| id | name     | price |\n|----|----------|-------|\n| 1  | Pen      | 10    |\n| 2  | Notebook | 25    |\n| 3  | Pencil   | 5     |\n\nQuery:\n```sql\nSELECT name, price FROM Products;\n```\nOutput:\n| name     | price |\n|----------|-------|\n| Pen      | 10    |\n| Notebook | 25    |\n| Pencil   | 5     |"
      }
    ],
    "coursename": "Intro to SQL"
  }
,

{
  "id": 2,
  "html_title": "select-statement-deep-dive",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "SELECT, FROM, and WHERE Basics",
      "bootstrap_title_desc": "The SELECT statement is used to retrieve data from a table.\n\n**Syntax:**\n```sql\nSELECT column1, column2 FROM table_name WHERE condition;\n```\n\n**Example:**\n```sql\nSELECT name, salary FROM Employees WHERE department = 'IT';\n```\nThis returns names and salaries of employees who work in the IT department."
    },
    {
      "bootstrap_title_heading": "Filtering with Logical Operators (AND, OR, NOT)",
      "bootstrap_title_desc": "**AND** means all conditions must be true.\n**OR** means at least one condition must be true.\n**NOT** negates a condition.\n\n**Example:**\n```sql\nSELECT name FROM Employees WHERE department = 'HR' AND salary > 40000;\n```\n\n```sql\nSELECT name FROM Employees WHERE department = 'Sales' OR department = 'Marketing';\n```\n\n```sql\nSELECT name FROM Employees WHERE NOT department = 'Finance';\n```"
    },
    {
      "bootstrap_title_heading": "Comparison Operators (=, <, >, BETWEEN, IN, LIKE)",
      "bootstrap_title_desc": "Use comparison operators to match data:\n- **=** Equal to\n- **<, >** Less/Greater than\n- **BETWEEN** Range\n- **IN** List of values\n- **LIKE** Pattern matching (with `%`)\n\n**Examples:**\n```sql\nSELECT name FROM Employees WHERE salary BETWEEN 30000 AND 50000;\n```\n```sql\nSELECT name FROM Employees WHERE department IN ('IT', 'Sales');\n```\n```sql\nSELECT name FROM Employees WHERE name LIKE 'A%';  -- names starting with A\n```"
    },
    {
      "bootstrap_title_heading": "Sorting Results with ORDER BY",
      "bootstrap_title_desc": "Use ORDER BY to sort results.\n- **ASC** (ascending, default)\n- **DESC** (descending)\n\n**Example:**\n```sql\nSELECT name, salary FROM Employees ORDER BY salary DESC;\n```\nThis returns the employee list sorted by salary from highest to lowest."
    },
    {
      "bootstrap_title_heading": "Aliasing Columns with AS",
      "bootstrap_title_desc": "Alias gives a temporary name to columns or tables for readability.\n\n**Example:**\n```sql\nSELECT name AS EmployeeName, salary AS MonthlySalary FROM Employees;\n```\nThis displays column headers as 'EmployeeName' and 'MonthlySalary' instead of their original names."
    },

  
  ],
  "coursename": "SELECT Statement"
},


{
  "id": 3,
  "html_title": "working-with-data",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "INSERT INTO – Add New Records",
      "bootstrap_title_desc": "**Syntax:**\n```sql\nINSERT INTO table_name (column1, column2, ...) VALUES (value1, value2, ...);\n```\n\n**Example:**\n```sql\nINSERT INTO Employees (name, department, salary)\nVALUES ('Frank', 'Finance', 47000);\n```\nThis adds a new employee named Frank to the Finance department."
    },
    {
      "bootstrap_title_heading": "UPDATE – Modify Existing Records",
      "bootstrap_title_desc": "**Syntax:**\n```sql\nUPDATE table_name SET column1 = value1 WHERE condition;\n```\n\n**Example:**\n```sql\nUPDATE Employees SET salary = 62000 WHERE name = 'Alice';\n```\nThis updates Alice’s salary to 62000."
    },
    {
      "bootstrap_title_heading": "DELETE – Remove Records",
      "bootstrap_title_desc": "**Syntax:**\n```sql\nDELETE FROM table_name WHERE condition;\n```\n\n**Example:**\n```sql\nDELETE FROM Employees WHERE department = 'Sales';\n```\nThis deletes all employees in the Sales department.\n\n**⚠️ Warning:** Always use WHERE clause to avoid deleting all records!"
    },
    {
      "bootstrap_title_heading": "Data Types in MS SQL",
      "bootstrap_title_desc": "Common data types:\n- **INT** – Whole numbers\n- **VARCHAR(n)** – Text with variable length (n characters)\n- **DATE** – Stores dates (e.g., 2025-05-06)\n- **DECIMAL(p,s)** – Precise decimal numbers (e.g., salary)\n\n**Example table:**\n```sql\nCREATE TABLE Employees (\n  id INT PRIMARY KEY IDENTITY(1,1),\n  name VARCHAR(50),\n  department VARCHAR(50),\n  salary DECIMAL(10,2),\n  hire_date DATE\n);\n```"
    },
    {
      "bootstrap_title_heading": "Handling NULL Values",
      "bootstrap_title_desc": "`NULL` represents missing or unknown data.\n\n- Use **IS NULL** or **IS NOT NULL** to check.\n\n**Examples:**\n```sql\nSELECT name FROM Employees WHERE salary IS NULL;\n```\n```sql\nUPDATE Employees SET salary = 50000 WHERE salary IS NULL;\n```\nThis updates all employees with missing salaries to 50000."
    }
  ],
  "coursename": "Working with Data"
},

{
  "id": 4,
  "html_title": "functions-and-calculations",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Arithmetic Operations in SQL",
      "bootstrap_title_desc": "You can perform basic math in SQL queries.\n\n**Example – Add 10% bonus to salary:**\n```sql\nSELECT name, salary, salary * 1.10 AS new_salary FROM Employees;\n```\nThis calculates each employee’s salary with a 10% bonus."
    },
    {
      "bootstrap_title_heading": "COUNT() – Count Records",
      "bootstrap_title_desc": "**Example – Total number of employees:**\n```sql\nSELECT COUNT(*) AS total_employees FROM Employees;\n```\nThis returns how many rows are in the Employees table."
    },
    {
      "bootstrap_title_heading": "AVG(), SUM(), MIN(), MAX() – Aggregations",
      "bootstrap_title_desc": "These functions help you analyze numeric data.\n\n**Examples:**\n```sql\nSELECT AVG(salary) AS avg_salary FROM Employees;\n```\n```sql\nSELECT SUM(salary) AS total_payroll FROM Employees;\n```\n```sql\nSELECT MIN(salary), MAX(salary) FROM Employees;\n```\nThese return average salary, total payroll, and min/max salary."
    },
    {
      "bootstrap_title_heading": "DISTINCT – Remove Duplicate Values",
      "bootstrap_title_desc": "**Example – List unique departments:**\n```sql\nSELECT DISTINCT department FROM Employees;\n```\nThis shows a list of different departments without duplicates."
    },
    {
      "bootstrap_title_heading": "GROUP BY – Aggregate by Category",
      "bootstrap_title_desc": "**Example – Average salary by department:**\n```sql\nSELECT department, AVG(salary) AS avg_salary\nFROM Employees\nGROUP BY department;\n```\nThis groups employees by department and shows the average salary in each group."
    }
  ],
  "coursename": "Functions & Calculations"
}
,


{
  "id": 5,
  "html_title": "advanced-filtering-and-aggregation",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "GROUP BY with Multiple Columns",
      "bootstrap_title_desc": "**Example – Group by department and hire year:**\n```sql\nSELECT department, YEAR(hire_date) AS hire_year, COUNT(*) AS total_hired\nFROM Employees\nGROUP BY department, YEAR(hire_date);\n```\nThis counts how many employees were hired each year per department."
    },
    {
      "bootstrap_title_heading": "HAVING vs WHERE – Filter Aggregate Results",
      "bootstrap_title_desc": "- **WHERE** filters rows *before* grouping.\n- **HAVING** filters *after* grouping.\n\n**Example – Show departments with average salary > 50,000:**\n```sql\nSELECT department, AVG(salary) AS avg_salary\nFROM Employees\nGROUP BY department\nHAVING AVG(salary) > 50000;\n```\nThis filters grouped results to show only departments with high average salaries."
    },
    {
      "bootstrap_title_heading": "Combining WHERE and HAVING",
      "bootstrap_title_desc": "Use **WHERE** to restrict input data, **HAVING** to restrict grouped data.\n\n**Example – IT employees hired after 2020, only show departments with average salary > 55,000:**\n```sql\nSELECT department, AVG(salary) AS avg_salary\nFROM Employees\nWHERE department = 'IT' AND hire_date > '2020-01-01'\nGROUP BY department\nHAVING AVG(salary) > 55000;\n```"
    },
    {
      "bootstrap_title_heading": "Real-World Query – Top Hiring Departments",
      "bootstrap_title_desc": "**Example – Departments with more than 2 hires:**\n```sql\nSELECT department, COUNT(*) AS num_hired\nFROM Employees\nGROUP BY department\nHAVING COUNT(*) > 2;\n```\nThis identifies departments that hired more than 2 employees."
    }
  ],
  "coursename": "Aggregation"
},

{
  "id": 6,
  "html_title": "joins-in-sql",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Understanding Table Relationships",
      "bootstrap_title_desc": "In relational databases, data is split across multiple related tables. JOINs help connect them.\n\n**Example Schema:**\n- `Employees (id, name, department_id)`\n- `Departments (id, department_name)`\n\nHere, `Employees.department_id` is a **foreign key** to `Departments.id`."
    },
    {
      "bootstrap_title_heading": "INNER JOIN – Matching Rows Only",
      "bootstrap_title_desc": "**Example – Join Employees with Departments:**\n```sql\nSELECT e.name, d.department_name\nFROM Employees e\nINNER JOIN Departments d ON e.department_id = d.id;\n```\nOnly employees with a matching department are shown."
    },
    {
      "bootstrap_title_heading": "LEFT JOIN – All from Left Table",
      "bootstrap_title_desc": "**Example – Show all employees, even if no department assigned:**\n```sql\nSELECT e.name, d.department_name\nFROM Employees e\nLEFT JOIN Departments d ON e.department_id = d.id;\n```\nIf there's no match in `Departments`, `department_name` will be NULL."
    },
    {
      "bootstrap_title_heading": "RIGHT JOIN – All from Right Table",
      "bootstrap_title_desc": "**Example – Show all departments, even if no employee is assigned:**\n```sql\nSELECT e.name, d.department_name\nFROM Employees e\nRIGHT JOIN Departments d ON e.department_id = d.id;\n```\nUseful to find departments without employees."
    },
    {
      "bootstrap_title_heading": "FULL OUTER JOIN – All Rows from Both Tables",
      "bootstrap_title_desc": "**Example – All employees and all departments, with or without matches:**\n```sql\nSELECT e.name, d.department_name\nFROM Employees e\nFULL OUTER JOIN Departments d ON e.department_id = d.id;\n```\nShows unmatched rows from both sides with NULLs."
    },
    {
      "bootstrap_title_heading": "Using Aliases and ON Clause",
      "bootstrap_title_desc": "Use table **aliases** like `e`, `d` to keep queries short and clear.\n\n`ON` defines the condition that links rows between tables.\n\n**Example:**\n```sql\nSELECT e.name, d.department_name\nFROM Employees e\nINNER JOIN Departments d\nON e.department_id = d.id;\n```"
    },


   {
  "bootstrap_title_heading": "Example",
  "bootstrap_title_desc": `
Employees Table:

| id | name    | department_id |
| -- | ------- | -------------- |
| 1  | Alice   | 1              |
| 2  | Bob     | 2              |
| 3  | Charlie | NULL           |
| 4  | Diana   | 3              |

Departments Table:

| id | department_name |
| -- | ---------------- |
| 1  | IT               |
| 2  | HR               |
| 3  | Marketing        |
| 4  | Finance          |

LEFT JOIN

SELECT e.name, d.department_name
FROM Employees e
LEFT JOIN Departments d ON e.department_id = d.id;

| name    | department_name |
| ------- | ---------------- |
| Alice   | IT               |
| Bob     | HR               |
| Charlie | NULL             |
| Diana   | Marketing        |
`
}

  ],
  "coursename": "JOINs in SQL"
},


{
  "id": 7,
  "html_title": "multi-table-queries",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Joining 3 or More Tables",
      "bootstrap_title_desc": "**Example – Join Employees, Departments, and Projects:**\n```sql\nSELECT e.name, d.department_name, p.project_name\nFROM Employees e\nJOIN Departments d ON e.department_id = d.id\nJOIN Projects p ON e.id = p.employee_id;\n```\nThis fetches employee names, their department, and the project they're working on."
    },
    {
      "bootstrap_title_heading": "Using Table Aliases for Readability",
      "bootstrap_title_desc": "Table **aliases** like `e`, `d`, `p` simplify long table names.\nThey make multi-table queries much cleaner.\n\n**Example:**\n```sql\nSELECT e.name, d.department_name\nFROM Employees e\nJOIN Departments d ON e.department_id = d.id;\n```"
    },
    {
      "bootstrap_title_heading": "Self JOIN – Comparing Rows in Same Table",
      "bootstrap_title_desc": "**Example – Find employee and their manager from same table:**\n```sql\nSELECT e.name AS employee, m.name AS manager\nFROM Employees e\nJOIN Employees m ON e.manager_id = m.id;\n```\nThis joins `Employees` to itself to match employees with their managers."
    },
    {
      "bootstrap_title_heading": "CASE WHEN – Conditional Logic in SQL",
      "bootstrap_title_desc": "**Example – Label salaries into categories:**\n```sql\nSELECT name, salary,\n  CASE\n    WHEN salary >= 60000 THEN 'High'\n    WHEN salary >= 40000 THEN 'Medium'\n    ELSE 'Low'\n  END AS salary_band\nFROM Employees;\n```\nThis creates custom labels based on salary ranges."
    },
    {
      "bootstrap_title_heading": "COALESCE – Replace NULL Values",
      "bootstrap_title_desc": "**Example – Show 'Unassigned' if department is NULL:**\n```sql\nSELECT name, COALESCE(department, 'Unassigned') AS dept_name\nFROM Employees;\n```\n`COALESCE()` returns the first non-NULL value in a list."
    },
    {
      "bootstrap_title_heading": "NULLIF – Avoid Division by Zero",
      "bootstrap_title_desc": "**Example – Safely calculate average score:**\n```sql\nSELECT name, total_score / NULLIF(attempts, 0) AS avg_score\nFROM Students;\n```\nReturns NULL instead of error if `attempts = 0`."
    }
  ],
  "coursename": "Multi-Table Queries"
},

{
  "id": 8,
  "html_title": "subqueries-and-nested-selects",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "What is a Subquery?",
      "bootstrap_title_desc": "A subquery is a query inside another query. It’s used to return data that the main query uses.\n\nExample:\n```sql\nSELECT name\nFROM Employees\nWHERE department_id IN (SELECT id FROM Departments WHERE location = 'New York');\n```\nThis finds employees in New York departments."
    },
    {
      "bootstrap_title_heading": "Subqueries in SELECT, FROM, WHERE",
      "bootstrap_title_desc": "**In SELECT:**\n```sql\nSELECT name, (SELECT COUNT(*) FROM Projects WHERE employee_id = e.id) AS project_count\nFROM Employees e;\n```\n\n**In FROM:**\n```sql\nSELECT dept_summary.department_name, dept_summary.total_employees\nFROM (\n  SELECT d.department_name, COUNT(e.id) AS total_employees\n  FROM Departments d\n  JOIN Employees e ON d.id = e.department_id\n  GROUP BY d.department_name\n) AS dept_summary;\n```\n\n**In WHERE:**\n```sql\nSELECT name\nFROM Employees\nWHERE salary > (SELECT AVG(salary) FROM Employees);\n```"
    },
    {
      "bootstrap_title_heading": "Correlated Subqueries",
      "bootstrap_title_desc": "A correlated subquery depends on the outer query.\n\nExample:\n```sql\nSELECT name, salary\nFROM Employees e\nWHERE salary > (\n  SELECT AVG(salary)\n  FROM Employees\n  WHERE department_id = e.department_id\n);\n```\nThis gets employees earning above their department average."
    },
    {
      "bootstrap_title_heading": "IN, EXISTS, NOT EXISTS",
      "bootstrap_title_desc": "**IN Example:**\n```sql\nSELECT name FROM Employees WHERE department_id IN (SELECT id FROM Departments WHERE active = 1);\n```\n\n**EXISTS Example:**\n```sql\nSELECT name FROM Employees e WHERE EXISTS (\n  SELECT 1 FROM Projects p WHERE p.employee_id = e.id\n);\n```\n\n**NOT EXISTS Example:**\n```sql\nSELECT name FROM Employees e WHERE NOT EXISTS (\n  SELECT 1 FROM Projects p WHERE p.employee_id = e.id\n);\n```"
    },
    {
      "bootstrap_title_heading": "Common Table Expressions (CTEs) using WITH",
      "bootstrap_title_desc": "**CTE Example:**\n```sql\nWITH DepartmentSalary AS (\n  SELECT department_id, AVG(salary) AS avg_salary\n  FROM Employees\n  GROUP BY department_id\n)\nSELECT e.name, e.salary, ds.avg_salary\nFROM Employees e\nJOIN DepartmentSalary ds ON e.department_id = ds.department_id;\n```\nCTEs make queries more readable and reusable."
    }
  ],
  "coursename": "Subqueries and Nested"
},

{
  "id": 9,
  "html_title": "data-modeling-and-table-design",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Creating Tables with CREATE TABLE",
      "bootstrap_title_desc": "Example:\n```sql\nCREATE TABLE Employees (\n  id INT PRIMARY KEY,\n  name NVARCHAR(100),\n  salary DECIMAL(10,2),\n  department_id INT\n);\n```\nThis creates an Employees table with id, name, salary, and department_id."
    },
    {
      "bootstrap_title_heading": "Primary Keys",
      "bootstrap_title_desc": "A PRIMARY KEY uniquely identifies each row.\n\nExample:\n```sql\nCREATE TABLE Departments (\n  id INT PRIMARY KEY,\n  department_name NVARCHAR(100)\n);\n```\n`id` is the primary key here."
    },
    {
      "bootstrap_title_heading": "Foreign Keys",
      "bootstrap_title_desc": "A FOREIGN KEY links to another table's primary key.\n\nExample:\n```sql\nALTER TABLE Employees\nADD CONSTRAINT fk_department FOREIGN KEY (department_id)\nREFERENCES Departments(id);\n```\nThis ensures that Employees.department_id matches Departments.id."
    },
    {
      "bootstrap_title_heading": "Constraints: NOT NULL, UNIQUE, DEFAULT, CHECK",
      "bootstrap_title_desc": "**NOT NULL:** value must be provided\n```sql\nname NVARCHAR(100) NOT NULL\n```\n\n**UNIQUE:** no duplicates allowed\n```sql\nemail NVARCHAR(255) UNIQUE\n```\n\n**DEFAULT:** set a default value\n```sql\nstatus NVARCHAR(20) DEFAULT 'Active'\n```\n\n**CHECK:** enforce a condition\n```sql\nsalary DECIMAL(10,2) CHECK (salary >= 0)\n```"
    },
    {
      "bootstrap_title_heading": "Altering Tables (ADD, DROP, RENAME Columns)",
      "bootstrap_title_desc": "**Add a column:**\n```sql\nALTER TABLE Employees ADD hire_date DATE;\n```\n\n**Drop a column:**\n```sql\nALTER TABLE Employees DROP COLUMN hire_date;\n```\n\n**Rename a column:**\n```sql\nEXEC sp_rename 'Employees.hire_date', 'joining_date', 'COLUMN';\n```"
    },
    {
      "bootstrap_title_heading": "Example: Full Table Design",
      "bootstrap_title_desc":  `
      CREATE TABLE Employees (
  id INT PRIMARY KEY,
  name NVARCHAR(100) NOT NULL,
  email NVARCHAR(255) UNIQUE,
  salary DECIMAL(10,2) CHECK (salary >= 0),
  department_id INT,
  status NVARCHAR(20) DEFAULT 'Active',
  CONSTRAINT fk_department FOREIGN KEY (department_id)
    REFERENCES Departments(id)
);

      `
    }
  ],
  "coursename": "Data Modeling"
},

{
  "id": 10,
  "html_title": "views-indexes-and-transactions",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Creating and Using VIEWS",
      "bootstrap_title_desc": "A VIEW is a virtual table based on a query.\n\nExample:\n```sql\nCREATE VIEW ActiveEmployees AS\nSELECT id, name, department_id\nFROM Employees\nWHERE status = 'Active';\n```\nUse it like a table:\n```sql\nSELECT * FROM ActiveEmployees;\n```"
    },
    {
      "bootstrap_title_heading": "Creating INDEXes for Performance",
      "bootstrap_title_desc": "An INDEX improves query speed on large tables.\n\nExample:\n```sql\nCREATE INDEX idx_employee_name ON Employees(name);\n```\nThis speeds up queries filtering by `name`.\n\nTo remove an index:\n```sql\nDROP INDEX idx_employee_name ON Employees;\n```"
    },
    {
      "bootstrap_title_heading": "Introduction to TRANSACTIONS",
      "bootstrap_title_desc": "A TRANSACTION ensures a set of SQL operations succeed together or fail together.\n\nExample:\n```sql\nBEGIN TRANSACTION;\n\nUPDATE Employees SET salary = salary * 1.10 WHERE department_id = 1;\nDELETE FROM Employees WHERE status = 'Terminated';\n\nCOMMIT TRANSACTION;\n```\nIf something goes wrong, you can undo with ROLLBACK."
    },
    {
      "bootstrap_title_heading": "COMMIT, ROLLBACK, and SAVEPOINT",
      "bootstrap_title_desc": "**COMMIT:** finalize the transaction.\n```sql\nCOMMIT TRANSACTION;\n```\n\n**ROLLBACK:** undo all changes.\n```sql\nROLLBACK TRANSACTION;\n```\n\n**SAVEPOINT:** set a partial rollback point.\n```sql\nSAVE TRANSACTION savepoint1;\n```\nRollback to it:\n```sql\nROLLBACK TRANSACTION savepoint1;\n```"
    }
  ],
  "coursename": " Views"
},


{
  "id": 11,
  "html_title": "advanced-sql-features",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Window Functions (ROW_NUMBER, RANK, etc.)",
      "bootstrap_title_desc": "Window functions perform calculations across rows related to the current row.\n\nExample:\n```sql\nSELECT name, department_id, salary,\n       ROW_NUMBER() OVER(PARTITION BY department_id ORDER BY salary DESC) AS row_num\nFROM Employees;\n```\nThis gives row numbers per department by salary order."
    },
    {
      "bootstrap_title_heading": "PARTITION BY and ORDER BY in Windows",
      "bootstrap_title_desc": "`PARTITION BY` splits data into groups, `ORDER BY` defines row order.\n\nExample:\n```sql\nSELECT name, department_id, salary,\n       RANK() OVER(PARTITION BY department_id ORDER BY salary DESC) AS dept_rank\nFROM Employees;\n```"
    },
    {
      "bootstrap_title_heading": "LEAD(), LAG(), FIRST_VALUE(), LAST_VALUE()",
      "bootstrap_title_desc": "**LEAD / LAG:** look ahead or behind rows.\n```sql\nSELECT name, salary,\n       LAG(salary) OVER(ORDER BY salary) AS prev_salary,\n       LEAD(salary) OVER(ORDER BY salary) AS next_salary\nFROM Employees;\n```\n\n**FIRST_VALUE / LAST_VALUE:** get first/last in partition.\n```sql\nSELECT name, salary,\n       FIRST_VALUE(salary) OVER(PARTITION BY department_id ORDER BY salary) AS min_salary\nFROM Employees;\n```"
    },
    {
      "bootstrap_title_heading": "CASE with Window Functions",
      "bootstrap_title_desc": "`CASE` lets you create conditional columns.\n\nExample:\n```sql\nSELECT name, salary,\n       CASE\n           WHEN salary > 5000 THEN 'High'\n           WHEN salary BETWEEN 3000 AND 5000 THEN 'Medium'\n           ELSE 'Low'\n       END AS salary_level\nFROM Employees;\n```"
    }
  ],
  "coursename": "Advanced SQL Features"
},


{
  "id": 12,
  "html_title": "real-world-project-best-practices",
  "bootstrap_head_desc": [
    {
      "bootstrap_title_heading": "Project: E-commerce Database",
      "bootstrap_title_desc": "Imagine you manage an e-commerce database with tables: Customers, Orders, Products, OrderDetails.\n\nExample query: Find total sales per customer.\n```sql\nSELECT c.CustomerName, SUM(od.Quantity * od.UnitPrice) AS TotalSales\nFROM Customers c\nJOIN Orders o ON c.CustomerID = o.CustomerID\nJOIN OrderDetails od ON o.OrderID = od.OrderID\nGROUP BY c.CustomerName;\n```"
    },
    {
      "bootstrap_title_heading": "Analyzing User Activity",
      "bootstrap_title_desc": "Find top 5 customers by total orders.\n```sql\nSELECT TOP 5 c.CustomerName, COUNT(o.OrderID) AS OrderCount\nFROM Customers c\nJOIN Orders o ON c.CustomerID = o.CustomerID\nGROUP BY c.CustomerName\nORDER BY OrderCount DESC;\n```"
    },
    {
      "bootstrap_title_heading": "Performance Tips (EXPLAIN, Query Optimization)",
      "bootstrap_title_desc": "Use the `SET STATISTICS IO ON;` or `SET SHOWPLAN_ALL ON;` commands in SQL Server to analyze queries.\n- Add indexes on frequently searched columns.\n- Avoid SELECT * in large queries.\n- Use appropriate JOINs and filters."
    },
    {
      "bootstrap_title_heading": "Exporting Results (CSV, JSON)",
      "bootstrap_title_desc": "In SQL Server Management Studio (SSMS):\n- Right-click query results → Save Results As → CSV.\n- For JSON export:\n```sql\nSELECT CustomerID, CustomerName\nFROM Customers\nFOR JSON AUTO;\n```"
    },
    {
      "bootstrap_title_heading": "Final Challenge Queries",
      "bootstrap_title_desc": "1. Find the average order value per customer.\n2. Find products with no sales.\n3. Calculate monthly sales totals.\n4. Identify customers who placed orders in the last 30 days."
    }
  ],
  "coursename": "Real-World Project & Best Practices"
}














];
export default SQL